/**
 * JSON:API pagination links
 */
export interface PaginationLinks {
  self?: string;
  first?: string;
  last?: string;
  prev?: string | null;
  next?: string | null;
}

/**
 * JSON:API response with pagination
 */
export interface PaginatedResponse<T> {
  data: T[];
  links?: PaginationLinks;
  meta?: {
    total_count?: number;
    page_count?: number;
    current_page?: number;
  };
}

/**
 * Options for paginated requests
 */
export interface PaginationOptions {
  /**
   * Page size (number of items per page)
   */
  pageSize?: number;

  /**
   * Maximum total items to fetch (default: unlimited for paginateAll, 10000 for paginateToArray)
   */
  limit?: number;

  /**
   * Maximum number of pages to fetch (default: 500)
   * Safety limit to prevent infinite pagination loops
   */
  maxPages?: number;

  /**
   * Optional logger for warnings. If not provided, falls back to console.
   */
  logger?: { warn: (...args: unknown[]) => void };
}

/**
 * Default maximum pages to prevent infinite pagination loops
 */
const DEFAULT_MAX_PAGES = 500;

/**
 * Async iterator for paginated results.
 * Security: Enforces max pages limit to prevent infinite pagination loops.
 */
export async function* paginateAll<T>(
  fetcher: (page: number, pageSize: number) => Promise<PaginatedResponse<T>>,
  options: PaginationOptions = {},
): AsyncIterableIterator<T> {
  const pageSize = options.pageSize || 20;
  const limit = options.limit;
  const maxPages = options.maxPages ?? DEFAULT_MAX_PAGES;
  const logger =
    options.logger ?? (typeof console !== "undefined" ? console : undefined);
  let page = 1;
  let totalYielded = 0;

  while (true) {
    // Security: Check page count to prevent infinite loops
    if (page > maxPages) {
      logger?.warn(
        `[GPT Platform SDK] Pagination stopped: reached maximum page limit (${maxPages}). ` +
          `Use options.maxPages to increase or options.limit to cap total items.`,
      );
      return;
    }

    const response = await fetcher(page, pageSize);

    // Yield each item
    for (const item of response.data) {
      yield item;
      totalYielded++;

      // Stop if we've hit the limit
      if (limit && totalYielded >= limit) {
        return;
      }
    }

    // Stop if no more pages
    if (!response.links?.next || response.data.length === 0) {
      break;
    }

    page++;
  }
}

/**
 * Default maximum items to prevent memory exhaustion
 */
const DEFAULT_LIMIT = 10000;

/**
 * Helper to collect all paginated results into an array
 * Security: Enforces default limit to prevent DoS via infinite pagination
 */
export async function paginateToArray<T>(
  fetcher: (page: number, pageSize: number) => Promise<PaginatedResponse<T>>,
  options: PaginationOptions = {},
): Promise<T[]> {
  const safeOptions = {
    pageSize: options.pageSize || 20,
    // Security: Apply default limit to prevent unbounded memory usage
    limit: options.limit ?? DEFAULT_LIMIT,
    logger: options.logger,
  };

  const results: T[] = [];
  for await (const item of paginateAll(fetcher, safeOptions)) {
    results.push(item);
  }
  return results;
}
